/**
 * Transport encoding for SunEditor HTML through succeed/WAF (avoids blocked patterns like `<style>`).
 * Must stay in sync with `WAF_SAFE_DESCRIPTION_PREFIX` in course-planner-service `wafSafeDescription.ts`.
 *
 * Encode on save (UI) when `release.shared.coursePlanner.coursePlannerModern` is on; decode before
 * persistence (CPS) so the DB stores plain HTML and read paths do not need to decode.
 */
export const WAF_SAFE_DESCRIPTION_PREFIX = '__cp_enc_desc__:';

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

function utf8ToBase64(value: string): string {
  return bytesToBase64(new TextEncoder().encode(value));
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function base64ToUtf8(value: string): string {
  return new TextDecoder().decode(base64ToBytes(value));
}

/**
 * Wraps UTF-8 HTML as prefix + Base64 for WAF-safe JSON bodies. Idempotent if already wrapped.
 * Empty / whitespace-only strings are returned unchanged (no encoding).
 */
export function encodeWafSafeDescriptionField(html: string): string {
  const trimmed = html.trim();
  if (trimmed === '') {
    return html;
  }
  if (html.startsWith(WAF_SAFE_DESCRIPTION_PREFIX)) {
    return html;
  }
  return WAF_SAFE_DESCRIPTION_PREFIX + utf8ToBase64(html);
}

/** Used only in unit tests — production read paths assume plain HTML from CPS. */
export function decodeWafSafeDescriptionField(html: string): string {
  if (!html.startsWith(WAF_SAFE_DESCRIPTION_PREFIX)) {
    return html;
  }
  const encoded = html.slice(WAF_SAFE_DESCRIPTION_PREFIX.length);
  if (!encoded) {
    return '';
  }
  try {
    return base64ToUtf8(encoded);
  } catch {
    return html;
  }
}

type RequirementWithOptionalDescription = { description?: string; [key: string]: unknown };

/**
 * Returns a shallow copy with WAF-safe encoding on known rich-text description fields only:
 * - top-level `description`
 * - each `requirements[].description`
 * - nested `course.description` (course catalog save wrapper)
 *
 * All other properties are left unchanged.
 */
export function encodeWafSafeDescriptionsInRequestBody<
  T extends {
    description?: unknown;
    requirements?: RequirementWithOptionalDescription[] | unknown;
    course?: { description?: string } & Record<string, unknown>;
  },
>(body: T): T {
  const next: T = { ...body };
  if (typeof next.description === 'string') {
    next.description = encodeWafSafeDescriptionField(next.description);
  }
  const reqs = next.requirements;
  if (Array.isArray(reqs)) {
    next.requirements = reqs.map((r: RequirementWithOptionalDescription) => {
      if (r === null || typeof r !== 'object' || typeof r.description !== 'string') {
        return r;
      }
      return { ...r, description: encodeWafSafeDescriptionField(r.description) };
    }) as T['requirements'];
  }
  const course = next.course;
  if (course && typeof course === 'object' && typeof course.description === 'string') {
    next.course = {
      ...course,
      description: encodeWafSafeDescriptionField(course.description),
    };
  }
  return next;
}
